import { navigate } from '@mr-yum/frontend-core/dist/services/routes'
import {
  Alert,
  Divider,
  ErrorFilledIcon,
  ErrorNotification,
} from '@mr-yum/frontend-ui'
import { CombinedError } from '@urql/core'
import { FLAG_AVC_DISPLAY_PASS_APP, FLAG_CREW_REVIEWS } from 'common/constants'
import { CartLayout } from 'components/Cart/CartLayout'
import { NotFound } from 'components/Common/NotFound'
import { isEonxEnvironment } from 'components/EonX/utils'
import { GuestReview } from 'components/GuestReview'
import { ReceiptEmailDrawer } from 'components/OrderHistory/ReceiptEmailDrawer'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useLogger } from 'hooks/useLogger'
import {
  PaymentProcessorType,
  useGetReviewQuery,
  useOrderConfirmationQuery,
  UserPartsFragment,
  useTabGuestConfirmationQuery,
  useVenueConfirmationQuery,
} from 'lib/gql'
import { getOrderingTypeFromSlug, OrderingTypeSlug, routes } from 'lib/routes'
import React, { ReactNode, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getLocalStorageWithExpiry } from 'stores/utils'

import { ConfirmationBanner } from './ConfirmationBanner'
import { ConfirmationBannerIcon } from './ConfirmationBannerIcon'
import { ConfirmationFooter } from './ConfirmationFooter'
import { ConfirmationLoader } from './ConfirmationLoader'
import { DRCLoyalty } from './DRCLoyalty'
import { Loyalty } from './Loyalty'
import { LoyaltyCTA } from './LoyaltyCTA'
import { OrderSummary } from './OrderSummary'
import { PassAppCTA } from './PassAppCTA'
import { ShareTabCTA } from './ShareTabCTA'
import { SurveyCTA } from './SurveyCTA'
import { TipJarBanner } from './TipJarBanner'
import { UserFinePrint } from './UserFinePrint'
import { UserHeading } from './UserHeading'
import { UserMessage } from './UserMessage'
import { ViewReceiptCTA } from './ViewReceiptCTA'

interface Props {
  venueSlug: string
  orderingTypeSlug?: OrderingTypeSlug
  orderId?: string
  tabId?: string
  user: UserPartsFragment
  showBack?: boolean
  showOrderSummary?: boolean
  showLoyaltyForm?: boolean
  showReceipt?: boolean
  showGuestReviews?: boolean
  showFooter?: boolean
  customError?: CombinedError
  customHeading?: ReactNode
  customMessage?: ReactNode
  customFinePrint?: ReactNode
  children?: ReactNode
}

export const ConfirmationPage = ({
  venueSlug,
  orderingTypeSlug,
  tabId,
  orderId,
  user,
  customError,
  customHeading,
  customMessage,
  customFinePrint,
  showBack = false,
  showOrderSummary = true,
  showLoyaltyForm = true,
  showReceipt = true,
  showGuestReviews = false,
  showFooter = true,
  children,
}: Props) => {
  const intl = useIntl()
  const [receiptDrawerOpen, setReceiptDrawerOpen] = useState(false)
  const [receiptDrawerNavigate, setReceiptDrawerNavigate] = useState(false)

  const paymentIntentError = getLocalStorageWithExpiry('paymentIntentError')

  const { value: reviewEnabled, ready: reviewFlagReady } =
    useFeatureFlag(FLAG_CREW_REVIEWS)

  const { value: avcDisplayPassApp, ready: avcDisplayPassAppFlagReady } =
    useFeatureFlag(FLAG_AVC_DISPLAY_PASS_APP)

  // Check that the user is not using the Pass app
  const isEonxEnv = isEonxEnvironment()
  const showPassApp =
    avcDisplayPassAppFlagReady && avcDisplayPassApp && !isEonxEnv

  const hasGuestReviews = reviewEnabled && showGuestReviews && !!orderId

  // orderingType optional, remove from query
  const [{ data: venueData, fetching: venueFetching, error: venueError }] =
    useVenueConfirmationQuery({
      variables: {
        venueSlug,
        orderingType: orderingTypeSlug
          ? getOrderingTypeFromSlug(orderingTypeSlug)
          : undefined,
      },
    })

  const [{ data: orderData, fetching: orderFetching, error: orderError }] =
    useOrderConfirmationQuery({
      pause: !orderId,
      variables: { orderId: orderId || '' },
    })

  const [{ data: tabData, fetching: tabFetching, error: tabError }] =
    useTabGuestConfirmationQuery({
      pause: !tabId,
      variables: { tabId: tabId || '' },
    })

  const [{ data: reviewData, fetching: reviewFetching, stale: reviewStale }] =
    useGetReviewQuery({
      variables: { orderId: orderId ?? '' },
      pause: !orderId,
    })

  const { logEvent } = useLogger()

  const handleTracking = () => {
    logEvent('Clicked PASS App CTA', {
      isEonxEnv,
      orderId: orderData?.guestOrderById?.id,
      venueSlug,
    })
  }

  const handleOnSuccess = useCallback(() => {
    if (receiptDrawerNavigate) {
      void navigate(routes.history, {
        venueSlug,
        goBack: true,
      })
    }
  }, [receiptDrawerNavigate, venueSlug])

  if (
    venueFetching ||
    orderFetching ||
    tabFetching ||
    reviewFetching ||
    !reviewFlagReady
  ) {
    return <ConfirmationLoader showBack={showBack} />
  }

  const venue = venueData?.guestVenue
  const order = orderData?.guestOrderById
  const review = reviewData?.getReview
  const tab = tabData?.getTabGuestConfirmation
  const email = user?.email
  const isChargeToRoom =
    order?.paymentProcessor === PaymentProcessorType.ChargeToRoom

  if (venueError || !venue) {
    return (
      <>
        <NotFound
          message={intl.formatMessage(
            {
              defaultMessage: 'Venue {venueSlug} not found',
              id: '2R567s',
            },
            { venueSlug },
          )}
          error={venueError}
        />
      </>
    )
  }

  const showSurveyLink =
    venue.surveyLink &&
    !venue.yumpingoVenueId &&
    !isEonxEnvironment() &&
    !hasGuestReviews

  const showTipJarBanner = venue.tipjarUrl
  const banner = venue.mobileBanner || venue.banner

  return (
    <>
      <CartLayout title={venue.name} showBack={showBack} showNavBar={false}>
        {[venueError, orderError, tabError, customError].map((error, index) =>
          error ? (
            <div className="m-2" key={index}>
              <ErrorNotification fullWidth key={index} error={error} />
            </div>
          ) : null,
        )}

        <ConfirmationBanner banner={banner} />

        <ConfirmationBannerIcon venueSlug={venueSlug} tabId={tabId} />

        <div className="space-y-2 p-4 pt-5">
          <UserHeading user={user} customHeading={customHeading} />

          <UserMessage
            user={user}
            showReceipt={showReceipt}
            openDrawer={() => {
              setReceiptDrawerNavigate(false)
              setReceiptDrawerOpen(true)
            }}
            customMessage={customMessage}
            viewReceiptCTA={
              showReceipt && !isEonxEnvironment() ? (
                <ViewReceiptCTA
                  venueSlug={venueSlug}
                  orderingTypeSlug={orderingTypeSlug}
                  orderId={orderId}
                  tabId={tabId}
                  email={email}
                  openDrawer={() => {
                    setReceiptDrawerOpen(true)
                    setReceiptDrawerNavigate(true)
                  }}
                />
              ) : undefined
            }
          />
        </div>

        {paymentIntentError && (
          <div className="px-4">
            <Alert
              variant="critical-subtle"
              icon={<ErrorFilledIcon />}
              fullWidth
              title={paymentIntentError}
            />
          </div>
        )}

        {hasGuestReviews && orderId && (
          <GuestReview
            rating={review?.rating ?? 0}
            reviewId={review?.id ?? null}
            notes={review?.notes}
            orderId={orderId}
            venueName={venue.name}
            disabled={reviewStale}
          />
        )}

        {showLoyaltyForm && <Loyalty venueSlug={venueSlug} user={user} />}
        {showLoyaltyForm && <DRCLoyalty />}

        {showPassApp && <PassAppCTA onClick={handleTracking} />}

        <div className="overflow-hidden">
          {showOrderSummary && order && (
            <div>
              <div className="pt-5">
                <h3 className="px-4 my-heading-sm">
                  <FormattedMessage defaultMessage="Your order" id="yLi5uF" />
                </h3>

                <OrderSummary order={order} venue={venue} />
              </div>
              <Divider />
            </div>
          )}

          {children}

          {tabId && tab && (
            <div>
              <ShareTabCTA
                tab={tab}
                venueSlug={venueSlug}
                tabId={tabId}
                orderId={orderId}
              />
              <Divider />
            </div>
          )}

          {showSurveyLink && (
            <div>
              <SurveyCTA
                surveyLink={venue.surveyLink}
                orderId={orderId}
                tabId={tabId}
              />

              <Divider />
            </div>
          )}

          <LoyaltyCTA
            venueSlug={venueSlug}
            venueId={venue.id}
            guestId={user.id}
          />

          {showTipJarBanner && order && (
            <div className="space-y-5 px-4 pt-9">
              <h3 className="my-heading-sm">
                <FormattedMessage
                  defaultMessage="Show your support"
                  id="liFlnO"
                />
              </h3>

              <TipJarBanner
                href={venue.tipjarUrl!}
                order={order}
                currency={venue.currency}
              />
            </div>
          )}

          {!isChargeToRoom && (
            <UserFinePrint customFinePrint={customFinePrint} />
          )}
        </div>

        {showFooter ? (
          <ConfirmationFooter
            tabId={tabId}
            orderId={orderId}
            venueSlug={venueSlug}
            orderingTypeSlug={orderingTypeSlug}
          />
        ) : (
          <div className="mb-4" />
        )}
      </CartLayout>

      {receiptDrawerOpen && (
        <ReceiptEmailDrawer
          venueSlug={venueSlug}
          isOpen={receiptDrawerOpen}
          onClose={() => setReceiptDrawerOpen(false)}
          onSuccess={handleOnSuccess}
          email={email}
          shouldNavigate={receiptDrawerNavigate}
        />
      )}
    </>
  )
}
